import {
  GridColDef,
  GridFilterModel,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid';

import React, { useEffect, useMemo, useState } from 'react';

import {
  ExpandLess,
  ExpandMore,
  NumbersRounded,
  SearchRounded,
  Warning,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Checkbox,
  Collapse,
  debounce,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import {
  useGetSubGroupListQuery,
  useLazyGetFolioUuidByRoomNoQuery,
  usePostTransferSubGroupMutation,
} from '../../../../api/front-office/roomposting.api.rtk';
import { getColor } from '../../../../assets/theme';
import ServerError from '../../../../components/animation/server-error';
import DataTable from '../../../../components/table/datatable';
import useModal from '../../../../hooks/useModal';
import useToast from '../../../../hooks/useToast';
import { useRoomPostingContext } from '../../containers/fo-room_posting.container';
import {
  RoomPostingCLPaymentBreakdownEntry,
  RoomPostingLedgerData,
  RoomPostingSubGroupFolioList,
  RoomPostingTransferFolio,
} from '../../interface/room-posting-interface';
import FORoomPostingDiscountForm from './drawer form/fo-roomposting-discount-form';
import FORoomPostingLineTransferForm from './drawer form/fo-roomposting-linetransfer-form';
import FORoomPostingModifyLineForm from './drawer form/fo-roomposting-modifyline-form';
import CustomToolTip from './fo-roomposting-toolstrip';

import useInformationDialog from '../../../../hooks/useInformationDialog';
import FoRoompostingItemremarksForm from './drawer form/fo-roomposting-itemremarks-form';

import useAppContext from '@hooks/useAppContext';
import { useAppSelector } from '@store/index';
import { selectFOSalesDate } from '@store/selector/app.selector';
import { formatNumber } from '@utils/format';
import CASARVoucher from '../../../../components/voucher/cas-ar-voucher';
import useDisclosure from '../../../../hooks/useDisclosure';
import FoRoompostingItemvoidForm from './drawer form/fo-roomposting-itemvoid-form';
import DialogMessage from '@components/dialog/dialog-message';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';

const RoomPostingDataTable = ({
  setVoid,
  isNetLocked = false,
  isFetchingLedger = false,
  isCityLedgerPosting = false,
  isShowCLTrans,
  bookingStatus,
  onCheckboxChange,
  isSpecificBooking,
  subGroupValue,
  setSubGroupValue,
}: {
  setVoid?: (func: () => void) => void;
  isNetLocked?: boolean;
  isFetchingLedger?: boolean;
  isCityLedgerPosting?: boolean;
  isShowCLTrans?: boolean;
  bookingStatus?: string;
  onCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isSpecificBooking?: boolean;
  subGroupValue: string;
  setSubGroupValue: React.Dispatch<React.SetStateAction<string>>;
}) => {
  //Get Initial Value of Context
  const {
    selectedFolioUuid,
    setSelectedFolioUuid,
    roomPostingLedgerList,
    setSelectedSubGroup,
    selectedSubGroup,
    useProperty,
    ledgerRefetch,
    srcNav,
    setSelectedLedgers,
    selectedLedgers,
    setCLEditedRows,
    clEditedRows,
  } = useRoomPostingContext();
  const { ModalWrapper, modalDisclosure } = useModal();
  const { openSecCodeDialog } = useAppContext();

  const [transIds, setTransIds] = useState('');
  const [selRemarks, setSelRemarks] = useState('');
  const { textColor } = getColor();
  const { toast } = useToast();
  const { DialogInformation, onShowInfoDialog } = useInformationDialog();
  const apiRef = useGridApiRef();
  const [showFolio, setShowFolio] = useState(true);
  const [errorTitle, setErrorTitle] = useState<string>('Warning');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [restoChargeItemList, setRestoChargeItemList] = useState<string[]>([]);
  // #region view options
  const {
    data: subGroupListData = [],
    isError: isErrorFolio,
    isLoading: isLoadingSubFolio,
  } = useGetSubGroupListQuery({}, { refetchOnMountOrArgChange: true });
  const [subGroupList, setSubGroupList] = useState<
    RoomPostingSubGroupFolioList[]
  >([]);

  const disclosures = {
    dialogMessage: useDisclosure(),
    restoChargeValidation: useDisclosure(),
  };

  const { dialogMessage, restoChargeValidation } = disclosures;

  // Define state for views as an array of strings
  const [isFetchingFilter, setIsFetchingFilter] = useState(false);
  // useEffect to handle data fetching and setting views

  // const [showReprintAR, setShowReprintAR] = useState(false);
  const printARDisclosure = useDisclosure();

  const [voidARNos, setVoidARs] = useState<string>('');

  const currentSalesDate = useAppSelector(selectFOSalesDate);

  const [isErrorSub, setIsErrorSub] = useState(false);

  useEffect(() => {
    // Remove duplicates based on the 'id' property
    const uniqueSubGroupList = subGroupListData?.data
      ? Array.from(new Set(subGroupListData.data.map((item) => item.id))).map(
          (id) => subGroupListData.data.find((item) => item.id === id),
        )
      : [];

    setSubGroupList(uniqueSubGroupList);
  }, [subGroupListData]);

  useEffect(() => {
    setIsErrorSub(false);
    // Check if the subGroupList has configured sub groups
    if (subGroupListData.data) {
      if (subGroupListData?.data.length <= 3) {
        setIsErrorSub(true);
        toast({
          message: 'Kindly configure sub folio list',
          severity: 'error',
        });
      }
    }

    // Set subGroupValue to the caption of the first sub-group or initialize as an empty string
    setSubGroupValue(subGroupList[0]?.caption || '');

    // Set selectedSubGroup to the first sub-group
    setSelectedSubGroup(0);
  }, [subGroupList]);
  // #endregion view options

  useEffect(() => {
    if (roomPostingLedgerList?.data?.roomNo) {
      setSelectedRoomNo(roomPostingLedgerList?.data?.roomNo);
    }
  }, [roomPostingLedgerList?.data?.roomNo]);

  // #region Select Room Number
  const [selectedDate, setSelectedDate] = useState<Moment | null>(moment());
  const [selectedRoomNo, setSelectedRoomNo] = useState<number | string>(
    roomPostingLedgerList?.data?.roomNo,
  );
  const [selectedLedgerItemType, setSelectedLedgerItemType] =
    useState<RoomPostingLedgerData>();

  //API CALL to get the Folio Details
  const [getFolioUuidByRoomNo, { isLoading, isError }] =
    useLazyGetFolioUuidByRoomNoQuery();

  const handleRoomNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRoomNo(event.target.value.replace(/[^a-zA-Z0-9]/g, ''));
  };
  const appliedSC = roomPostingLedgerList?.data?.ledgerResult?.[0]
    ?.service_charge_rate
    ? Math.round(
        Number(roomPostingLedgerList.data.ledgerResult[0].service_charge_rate) *
          100,
      )
    : 0;
  //Get Room Number Folio
  const handleRoomNoKeyEvent = async (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== 'Enter') return;

    if (!selectedRoomNo) {
      onShowInfoDialog({
        desc: 'Please enter a room number.',
      });

      return;
    }
    try {
      // Fetch entered Room Number
      setIsFetchingFilter(true);
      const { data: retrievedFolio, isLoading } =
        await getFolioUuidByRoomNo(selectedRoomNo);
      setIsFetchingFilter(isLoading);
      if (!retrievedFolio.data[0]?.folioUuid) {
        setSelectedFolioUuid('');
        toast({
          message: 'Please enter the room number with in-house status.',
          severity: 'warning',
          duration: 1000,
        });
        return;
      }

      //Update selected Folio
      const folio = retrievedFolio.data[0]?.folioUuid;
      setSelectedFolioUuid(folio);
    } catch (error) {
      toast({
        message: 'An error occurred during Room Number Search.',
        severity: 'error',
      });
    } finally {
      setIsFetchingFilter(false);
    }
  };

  //Search on Datatable
  const updateSearchValue = useMemo(() => {
    return debounce((newValue) => {
      apiRef?.current?.setQuickFilterValues(newValue);
    }, 500);
  }, [apiRef?.current]);

  const handleSearchFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.value;
    updateSearchValue(newValue);
  };

  const handleSearchViewChange = (selSubGroup: string) => {
    setIsFetchingFilter(true);
    const subGroupID = getSubGroupID(selSubGroup);

    setSelectedSubGroup(subGroupID);
    setIsFetchingFilter(false);
  };

  //function to get SubGroup ID
  const getSubGroupID = (subGroupCaption: string): number => {
    let subGroupID = 0; // Default value
    try {
      const selectedSubGroup = subGroupList?.find(
        (subGroup) => subGroup?.caption === subGroupCaption,
      );
      if (selectedSubGroup) {
        if (selectedSubGroup.id < 97) {
          subGroupID = selectedSubGroup.id;
        }
      } else {
        subGroupID = 0;
      }
    } catch (error) {
      console.error('Error Select error: ' + subGroupCaption);
    }

    return subGroupID;
  };

  //function to get total entries per sub group
  const getSubGroupCount = (subGroupCaption: string): number => {
    let subGroupCount = 0;
    const subGroupID = getSubGroupID(subGroupCaption);
    // Check if data exists
    if (roomPostingLedgerList.data?.ledgerResult) {
      // Filter and count occurrences of the specified sub_group
      subGroupCount = roomPostingLedgerList.data.ledgerResult.filter(
        (row) => row.sub_group === subGroupID.toString(),
      ).length;
    }

    return subGroupCount;
  };
  // Search by Date on Datatable
  const [isSearching, setIsSearching] = useState(false);

  const updateSearchDate = useMemo(() => {
    return debounce((newValue) => {
      setIsSearching(true);

      const formattedDate = moment(newValue).format('YYYY-MM-DD');
      const todayFormattedDate = moment(currentSalesDate).format('YYYY-MM-DD');

      const dateVal =
        todayFormattedDate === formattedDate ? null : formattedDate;

      const newFilterModel: GridFilterModel = {
        items: dateVal
          ? [
              {
                field: 'posting_date',
                operator: 'is',
                value: dateVal,
              },
            ]
          : [],
      };

      apiRef?.current?.setFilterModel(newFilterModel);

      setTimeout(() => setIsSearching(false), 300);
    }, 500);
  }, [apiRef?.current]);

  const handleOnChangeDateEvent = (
    newDateSelected: React.SetStateAction<Moment | null>,
  ) => {
    setSelectedDate(newDateSelected);
    updateSearchDate(newDateSelected);
  };

  //View Detailed Folio on Datatable(checkbox)
  const handleSelectionChange = (selection: GridRowSelectionModel) => {
    setSelectedLedgers(selection);
  };

  //AutoScroll down the Datatable
  //UseCallBack when changes made on LedgerList
  useEffect(() => {
    if (apiRef.current) {
      try {
        //total rows from API
        const lastRowIndex = apiRef?.current?.getRowsCount() || 0 - 1;

        setIsFetchingFilter(true);

        if (lastRowIndex > 0) {
          // Set Page if more than 100
          if (lastRowIndex > 100) {
            const setPageRow = Math.floor(lastRowIndex / 100);
            const setPageRowF =
              lastRowIndex % 100 === 0 ? setPageRow : setPageRow + 1;

            apiRef.current.setPage(setPageRowF);
          }

          setTimeout(() => {
            // Auto scroll down
            if (lastRowIndex > 8) {
              apiRef.current.scrollToIndexes({ rowIndex: lastRowIndex });
            }
            setIsFetchingFilter(false);
          }, 500);
        }
      } catch (error) {
        console.error('Error fetching rows or scrolling: ', error);
        setIsFetchingFilter(false);
      }
    }
    setIsFetchingFilter(false);
  }, [apiRef, roomPostingLedgerList]);
  // #endregion Select Room Number

  // Filter city ledger items only (on account posting)
  const [clOnlyRows, setCLOnlyRows] = useState(
    roomPostingLedgerList.data?.ledgerResult.map((r) => ({
      ...r,
      originalCollection: r.cl_collection,
    })) ?? [],
  );
  const [showCLTrans, setShowCLTrans] = useState(isShowCLTrans);

  useEffect(() => {
    const refreshedLedgerList =
      roomPostingLedgerList.data?.ledgerResult
        .filter(
          (row) =>
            row.item === 'City Ledger' && showCLTrans && isCityLedgerPosting,
        )
        .map((r) => ({
          ...r,
          originalCollection: r.cl_collection,
        })) ?? [];
    setCLOnlyRows(refreshedLedgerList);
  }, [roomPostingLedgerList, showCLTrans]);

  const showOnlyCityLedgerTransactions = (event) => {
    const checked = event.target.checked;
    setShowCLTrans(checked);

    if (checked) {
      setCLOnlyRows(
        roomPostingLedgerList.data?.ledgerResult
          .filter((row) => row.item === 'City Ledger')
          .map((r) => ({
            ...r,
            originalCollection: r.cl_collection,
          })) ?? [],
      );
    } else {
      setCLOnlyRows(
        roomPostingLedgerList.data?.ledgerResult.map((r) => ({
          ...r,
          originalCollection: r.cl_collection,
        })) ?? [],
      );
    }
  };

  const sourceRows = useMemo(
    () =>
      isCityLedgerPosting
        ? (clOnlyRows?.length ?? 0) > 0
          ? clOnlyRows
          : (roomPostingLedgerList.data?.ledgerResult ?? [])
        : (roomPostingLedgerList.data?.ledgerResult ?? []),
    [isCityLedgerPosting, clOnlyRows, roomPostingLedgerList.data],
  );

  const clEditedMap = useMemo(
    () =>
      new Map(
        clEditedRows?.map((item) => [
          item.ledgerTransactionID,
          item.currentCollection,
        ]) ?? [],
      ),
    [clEditedRows],
  );

  /**
   * Steps:
   * 1. Filter valid transactions
   * 2. Apply subgroup filtering
   * 3. Format posting date
   * 4. Resolve collection value
   */
  const initialData = useMemo(() => {
    if (!sourceRows.length) return [];

    return sourceRows
      .filter((row) => row.transaction_id)
      .filter((row) => {
        if (subGroupValue === 'All') return true;

        if (subGroupValue === 'Charges' || subGroupValue === 'Payments') {
          return (
            row.dr_cr_adj === (subGroupValue === 'Charges' ? 'Credit' : 'Debit')
          );
        }

        return row.sub_group === String(selectedSubGroup);
      })
      .map((row) => {
        const editedCollection = clEditedMap.get(row.transaction_id);

        return {
          ...row,
          posting_date: row.posting_date,
          collection: showCLTrans
            ? (editedCollection ?? row.cl_collection)
            : (editedCollection ?? row.collection),
        };
      });
  }, [sourceRows, clEditedMap, subGroupValue, selectedSubGroup, showCLTrans]);

  const totalAmount = useMemo(() => {
    if (!selectedLedgers?.length || !initialData.length) return 0;

    return initialData
      .filter((row) => selectedLedgers?.includes(row.transaction_id))
      .reduce((sum, row) => {
        const charge = Number(row.charges);
        return sum + (Number.isFinite(charge) ? charge : 0);
      }, 0);
  }, [initialData, selectedLedgers]);

  // #region Datatable Configuration
  //Column List for datatable(identify the columns)
  const RoomPostingList: GridColDef[] = [
    {
      field: 'posting_date',
      headerName: 'Date',
      minWidth: 100,
      width: 100,
      type: 'date',
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: ({ value }) => moment(value).format('MMM DD, YYYY'),
      //type: 'date',
      // valueGetter: ({ value }) => value && new Date(value),
    },
    {
      field: 'item',
      headerName: 'Item/Service',
      minWidth: 200,
      // flex: 1,
    },
    {
      field: 'gross_amount',
      headerName: 'Gross Amount',
      minWidth: 140,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'adjustments',
      headerName: 'Adjustments',
      minWidth: 124,
      align: 'right',
      headerAlign: 'right',
      // flex: 1,
      valueFormatter: ({ value }) => formatNumber(value, 2, 2),
      renderCell: (params) => (
        <CustomToolTip
          titleBreakdown={`Adjustments`}
          rowValue={params.formattedValue}
          rowData={params.row}
        />
      ),
    },
    {
      field: 'discount',
      headerName: 'Disc',
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'collection',
      headerName: 'Collections',
      minWidth: 114,
      align: 'right',
      headerAlign: 'right',
      type: 'number',
      editable: true,
      valueFormatter: ({ value }) => formatNumber(value, 2, 2),
      renderCell: (params) => (
        <CustomToolTip
          titleBreakdown='Collection'
          rowValue={params.formattedValue}
          rowData={params.row}
        />
      ),
      renderEditCell: (params) => (
        <TextField
          type='number'
          value={params.value ?? ''}
          onChange={(e) => {
            const val = e.target.value === '' ? '' : e.target.value;
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: val,
            });
          }}
          autoFocus
        />
      ),
    },

    {
      field: 'charges',
      headerName: 'Charges',
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      // flex: 1,
      valueFormatter: ({ value }) => formatNumber(value, 2, 2),
      renderCell: (params) => (
        <CustomToolTip
          titleBreakdown={`Charges`}
          rowValue={params.formattedValue}
          rowData={params.row}
        />
      ),
    },

    {
      field: 'net_amount',
      headerName: 'Net Amount',
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => formatNumber(value, 2, 2),
    },

    {
      field: 'vat',
      headerName: 'VAT',
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
    },

    {
      field: 'local_tax',
      headerName: 'Local Tax',
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
    },

    {
      field: 'service_charge',
      headerName: 'Service Charge',
      minWidth: 138,
      align: 'right',
      headerAlign: 'right',
    },

    {
      field: 'outlet',
      headerName: 'Outlet',
      minWidth: 120,
    },
    {
      field: 'line_remarks',
      headerName: 'Posting Remarks',
      minWidth: 300,
      width: 300,
      renderCell: (params) => {
        const value = (params.value || '').replace(/\s-\s/g, '\n- ');

        return (
          <Tooltip title={value} arrow placement='top'>
            <Box
              sx={{
                whiteSpace: 'pre-line',
                overflow: 'hidden',
                textOverflow: 'ellipsis',

                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',

                lineHeight: 1.4,
                maxHeight: '2.8em',
                wordBreak: 'break-word',
                width: '100%',
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
    },
    // {
    //   field: 'referenceID',
    //   headerName: 'Reference',
    //   minWidth: 250,
    //   width: 250,
    // },
    {
      field: 'discount_remarks',
      headerName: 'Discount Remarks',
      minWidth: 300,
      width: 300,
      renderCell: (params) => {
        const value = (params.value || '').replace(/\s-\s/g, '\n- ');

        return (
          <Tooltip title={value} arrow placement='top'>
            <Box
              sx={{
                whiteSpace: 'pre-line',
                overflow: 'hidden',
                textOverflow: 'ellipsis',

                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',

                lineHeight: 1.4,
                maxHeight: '2.8em',
                wordBreak: 'break-word',
                width: '100%',
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'category_type',
      headerName: 'Type',
      minWidth: 100,
    },
    {
      field: 'cashier',
      headerName: 'Cashier',
      minWidth: 100,
    },
    {
      field: 'sub_group',
      headerName: 'Sub Group',
      minWidth: 100,
      hideable: true,
    },
    {
      field: 'print_id',
      headerName: 'Slip No',
      minWidth: 100,
      hideable: true,
    },
    {
      field: 'dr_cr_adj',
      headerName: 'Type',
      minWidth: 100,
      hideable: true,
    },
    // {
    //   field: 'voided',
    //   headerName: 'Voided?',
    //   minWidth: 100,
    //   hideable: true,
    // },
  ];
  //View Detailed Folio Column Filter
  const visibleColumns = RoomPostingList.filter(
    (RoomPostingColumns) =>
      // RoomPostingColumns.field !== 'gross_amount' &&
      RoomPostingColumns.field !== 'vat' &&
      RoomPostingColumns.field !== 'net_amount' &&
      // RoomPostingColumns.field !== 'discount' &&
      RoomPostingColumns.field !== 'local_tax' &&
      RoomPostingColumns.field !== 'service_charge' &&
      RoomPostingColumns.field !== 'net' &&
      RoomPostingColumns.field !== 'outlet' &&
      RoomPostingColumns.field !== 'voided' &&
      RoomPostingColumns.field !== 'category_type' &&
      RoomPostingColumns.field !== 'cashier' &&
      RoomPostingColumns.field !== 'print_id',
  );

  // View Detailed Folio CheckBox
  const [settingsInputValue, setSettingsInputValue] = useState('');
  const [isDetailedFolioChecked, setIsDetailedFolioChecked] = useState(false);
  const [filteredColumns, setFilteredColumns] =
    useState<GridColDef[]>(visibleColumns);

  const handleDetailedFolioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked: boolean = event.target.checked;
    setIsDetailedFolioChecked(isChecked);

    if (isChecked) {
      setFilteredColumns(RoomPostingList);
    } else {
      setFilteredColumns(visibleColumns);
    }
  };
  // #endregion Datatable Configuration

  // #region Context Menu
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    let target = event.target as HTMLElement;

    const rowElement = target.closest('.MuiDataGrid-row');
    const id = rowElement?.getAttribute('data-id');

    if (!id) {
      setContextMenu(null);
      return;
    }
    if (id && !selectedLedgers.includes(id)) {
      setSelectedLedgers((prev) => [...prev, id]);
    }
    const selectedItem = roomPostingLedgerList.data?.ledgerResult.find(
      (row) => row.transaction_id === id,
    );

    setSelectedLedgerItemType(selectedItem); // ✅ store in state

    // ❌ CREDIT → DO NOT OPEN MENU
    if (
      selectedItem &&
      isCityLedgerPosting &&
      selectedItem.dr_cr_adj === 'Credit'
    ) {
      setContextMenu(null);
      return;
    }

    // ✅ otherwise open
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const [selectedContextMenu, setSelectedContextMenu] = useState('');
  const [selectedWithVAT, setSelectedWithVAT] = useState(false);
  const [selectedWithDiscount, setSelectedWithDiscount] = useState(false);
  const handleSelectedContext = (menuItem: string) => {
    //close if sub menu is not open
    if (subContextMenu == null) {
      setContextMenu(null);
    }

    if (!menuItem.length) return;

    if (selectedLedgers.length === 0) {
      onShowInfoDialog({
        desc: `Please select an item(s) to proceed.`,
      });
      return;
    }

    const selectedItems = getSelectedItems();
    let selectedIDs = '';
    //check today transactions
    const isToday = selectedItems.some((row) =>
      moment(row.posting_date).isBefore(moment(currentSalesDate), 'day'),
    );

    const withDiscounts = selectedItems.some(
      (row) => parseInt(row.discount, 10) > 0,
    );
    const isCredit = selectedItems.some((row) => row.dr_cr_adj !== 'Credit');
    const withVAT = selectedItems.some((row) => parseInt(row.vat, 10) > 0);
    const isVoided = selectedItems.some((row) => row.voided === 'Yes');
    // const isCSSlip = selectedItems.some((row) => row.print_id !== null);

    setSelectedWithDiscount(withDiscounts);
    setSelectedWithVAT(withVAT);

    switch (menuItem) {
      //Apply Discounts Validation
      case 'AD':
        if (withDiscounts || isCredit) {
          onShowInfoDialog({
            desc: withDiscounts
              ? `Some items have already been discounted.`
              : `Discounts may only be applied to transactions involving charges.`,
          });

          return;
        }

        if (isToday) {
          onShowInfoDialog({
            desc: `Previous day's entry cannot be discounted. \n \nIf discount is necessary, please use an adjustment today to offset the amount.`,
          });
          return;
        }

        if (isVoided) {
          onShowInfoDialog({
            desc: `Some of transactions are already voided.`,
          });
          return;
        }

        break;

      //Modify Line Validations
      case 'ML':
        if (isCredit) {
          onShowInfoDialog({
            desc: `Modify line may only be applied to transactions involving charges.`,
          });

          return;
        }

        if (isToday) {
          onShowInfoDialog({
            desc: `Previous day's entry cannot be modify line.`,
          });
          return;
        }

        if (isVoided) {
          onShowInfoDialog({
            desc: `Some of transactions are already voided.`,
          });
          return;
        }
        break;

      //Line Transfer Validations
      case 'LT':
        if (isVoided) {
          onShowInfoDialog({
            desc: `Some of transactions are already voided.`,
          });
          return;
        }
        break;

      default:
        onShowInfoDialog({
          desc: `Please select an action`,
        });
        return;
    }

    selectedIDs = selectedItems.map((row) => String(row._id)).join(',');
    setTransIds(selectedIDs);
    modalDisclosure.onToggle();
    setSelectedContextMenu(menuItem);
  };
  const menuMode = useMemo(() => {
    //CL
    if (
      selectedLedgerItemType &&
      isCityLedgerPosting &&
      selectedLedgerItemType?.dr_cr_adj === 'Debit'
    ) {
      return 'DEBIT';
    }
    //CL
    if (
      selectedLedgerItemType &&
      isCityLedgerPosting &&
      selectedLedgerItemType?.dr_cr_adj === 'Credit'
    ) {
      return 'CREDIT';
    }

    return 'ALL';
  }, [selectedLedgerItemType, isCityLedgerPosting]);
  const handleClose = () => {
    //close if sub menu is not open
    if (subContextMenu == null) {
      setContextMenu(null);
    }
  };

  const [subContextMenu, setSubContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const handleSubContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    setSubContextMenu(
      subContextMenu === null
        ? { mouseX: event.clientX + 50, mouseY: event.clientY + -1 }
        : null,
    );
  };

  // API Transfer Folio
  const [postTransferFolio, { isLoading: isLoadingTransfer }] =
    usePostTransferSubGroupMutation();

  const handleSubMenuClose = async (menuItem: string) => {
    setSubContextMenu(null);
    setContextMenu(null);

    if (!menuItem) {
      return;
    }

    const menuSubGroupID = getSubGroupID(menuItem);
    if (menuSubGroupID < 0) {
      return;
    }

    if (selectedLedgers.length === 0) {
      onShowInfoDialog({
        desc: `Please select an item(s) to proceed.`,
      });
      return;
    }

    const selectedItems = getSelectedItems();
    const selectedIDs = selectedItems
      .filter((row) => {
        const subGroupInt = row.sub_group;
        return subGroupInt !== menuSubGroupID;
      })
      .map((item) => String(item._id))
      .join(',');

    setTransIds(selectedIDs);

    if (!selectedIDs) {
      onShowInfoDialog({
        desc: `Some of the selected items have already been assigned to ${menuItem}`,
      });

      return;
    }

    const postTransfer: RoomPostingTransferFolio = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            sub_group_list: [
              {
                identifier_list: selectedIDs,
                sub_group_id: menuSubGroupID,
              },
            ],
          },
        },
      ],
    };

    await postTransferFolio(postTransfer)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: `Successfully transferred to ${menuItem}`,
        });
        handleClose();
        ledgerRefetch();
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred transferred to ${menuItem}: ${error}`,
        }),
      );
  };

  // #endregion Context Menu
  const getSelectedItems = () => {
    return selectedLedgers
      .map((trans_id) =>
        roomPostingLedgerList.data?.ledgerResult.find(
          (row) => row.transaction_id === trans_id,
        ),
      )
      .filter((row): row is RoomPostingLedgerData => !!row);
  };

  const handleRowDoubleClick = (selectedIDs: string, remarks: string) => {
    //close if sub menu is not open

    if (selectedIDs.length === 0) {
      onShowInfoDialog({
        desc: `Please select an item(s) to proceed.`,
      });
      return;
    }
    if (selectedIDs.length > 0) {
      setTransIds(selectedIDs);
      setSelRemarks(remarks);
      modalDisclosure.onToggle();
      setSelectedContextMenu('ER');
    }
  };

  const handleVoidItem = async () => {
    if (selectedLedgers.length === 0) {
      onShowInfoDialog({
        desc: `Please select an item(s) to proceed.`,
      });
      return;
    }
    const selectedItems = getSelectedItems();

    const isVoided = selectedItems.some((row) => row.voided === 'Yes');

    if (isVoided) {
      onShowInfoDialog({
        desc: `Some of transactions are already voided.`,
      });
      return;
    }

    const isCSSlip = selectedItems.some((row) => row.print_id !== null);

    if (isCSSlip) {
      const filteredCSSlip = roomPostingLedgerList?.data?.ledgerResult?.filter(
        (item) =>
          selectedItems.some(
            (row) => row.print_id !== null && row.print_id === item.print_id,
          ),
      );
      const filteredSelectedItems = selectedItems.filter(
        (item) => item.print_id !== null && item.print_id === item.print_id,
      );
      const uniquePrintIds = [
        ...new Set(
          selectedItems
            .filter((item) => item.print_id !== null)
            .map((item) => item.print_id),
        ),
      ]
        .map((slip) => `• ${slip}`)
        .join('\n');

      const countfilteredCSSlip = filteredCSSlip?.length || 0;
      const countfilteredSelectedItems = filteredSelectedItems?.length || 0;

      if (countfilteredCSSlip !== countfilteredSelectedItems) {
        onShowInfoDialog({
          desc:
            `The selected items include charges from the Charging Station.` +
            `\n\nTo proceed with the transaction, please ensure all items with the following slip number/s are selected:\n\n` +
            uniquePrintIds +
            `\n\nYou may enable 'View Detailed Folio' to review the slip numbers.\n\n`,
        });
        return;
      }
    }

    // Only Front Office items can be voided here. POS items must be voided from POS.
    // Keep the FO-only list separate from the full selection so the void API never receives POS ids.
    const frontOfficeItems = selectedItems.filter((item) => item.is_fo === 1);
    const excludedPosItems = selectedItems.filter((item) => item.is_fo !== 1);
    const frontOfficeIDs = frontOfficeItems
      .map((row) => String(row._id))
      .join(',');

    // POS validation uses the full selection, not the FO-only list.
    const hasExcludedPosItems = excludedPosItems.length > 0;

    if (hasExcludedPosItems) {
      if (frontOfficeItems.length === 0) {
        setErrorTitle('Unable to Void Resto Charge(s)');
        setErrorMessage(
          'These resto charge(s) were generated from POS and cannot be voided from the Front Office. </br></br> Please process the void transaction from the POS system to ensure proper transaction handling.',
        );
        dialogMessage.onOpen();
      } else {
        setRestoChargeItemList(
          excludedPosItems.map((r) => r.invoice_no || r.item),
        );
        restoChargeValidation.onOpen();
      }
      return;
    }

    setTransIds(frontOfficeIDs);
    setSelectedContextMenu('VD');
    modalDisclosure.onToggle();
  };

  useEffect(() => {
    if (setVoid) {
      setVoid(handleVoidItem);
    }
  }, [setVoid]);
  return (
    <>
      <Box position='relative'>
        {/* MAIN */}
        <Stack
          sx={{
            opacity: isNetLocked ? 0.5 : 'unset',
            pointerEvents: isNetLocked ? 'none' : 'unset',
            width: '100%',
          }}
          direction={{ xs: 'column', lg: 'row' }}
        >
          {/* DATATABLE */}
          <Box
            sx={{
              width: { xs: '100%', lg: '80%' },
              p: 2,
              oveflow: { xs: 'auto', lg: 'hidden' },
            }}
          >
            {isError || isErrorFolio || isErrorSub ? (
              <Stack justifyContent='center' alignItems='center'>
                <ServerError
                  sx={{
                    position: 'unset',
                    transform: 'unset',
                  }}
                />
              </Stack>
            ) : (
              <Stack height='100%' gap={2}>
                {/* Search View */}
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  gap={1}
                  justifyContent='space-between'
                >
                  <Stack
                    sx={{
                      justifyContent: 'flex-start', // Align items to the start (left)
                      gap: '2px',
                    }}
                    direction={{ xs: 'column', lg: 'row' }}
                  >
                    <TextField
                      id='search'
                      label='Search'
                      type='search'
                      variant='outlined'
                      size='small'
                      sx={{
                        pr: '5px',
                        pt: '2px',
                        minWidth: '200px',
                        width: { xs: '100%', lg: '30%' },
                        opacity: isNetLocked ? 0.5 : 'unset',
                        pointerEvents: isNetLocked ? 'none' : 'unset',
                      }}
                      autoComplete='off'
                      onChange={handleSearchFilterChange}
                      InputProps={{
                        endAdornment: <SearchRounded />,
                      }}
                      disabled={!selectedFolioUuid}
                    />

                    {!isCityLedgerPosting && (
                      <Autocomplete
                        id='view'
                        disableClearable
                        value={subGroupValue}
                        onChange={(
                          _event: React.SyntheticEvent<Element, Event>,
                          newValue: string,
                        ) => {
                          setSubGroupValue(newValue);
                          handleSearchViewChange(newValue);
                        }}
                        inputValue={settingsInputValue}
                        onInputChange={(
                          _event: React.SyntheticEvent<Element, Event>,
                          newInputValue: string,
                        ) => {
                          setSettingsInputValue(newInputValue);
                        }}
                        options={(subGroupList || []).map(
                          (option) => option?.caption || '',
                        )}
                        renderInput={(params) => (
                          <TextField
                            sx={{
                              pr: '5px',
                              pt: { xs: '6px', lg: '2px' },
                              minWidth: '200px',
                              width: { xs: '100%', lg: '30%' },
                            }}
                            {...params}
                            label='View'
                            size='small'
                            SelectProps={{
                              native: true,
                            }}
                          >
                            {(subGroupList || []).map((option) => (
                              <option
                                key={option?.caption}
                                value={option?.caption}
                              >
                                {option?.caption}
                              </option>
                            ))}
                          </TextField>
                        )}
                        sx={{
                          pt: { xs: '10px', lg: '0px' },
                          opacity: isNetLocked ? 0.5 : 'unset',
                          pointerEvents: isNetLocked ? 'none' : 'unset',
                        }}
                      />
                    )}
                  </Stack>
                  <Stack
                    sx={{
                      justifyContent: 'flex-end',
                      gap: { xs: '0px', lg: '2px' },
                    }}
                    direction='row'
                  >
                    {isCityLedgerPosting && (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              defaultChecked={isShowCLTrans}
                              onChange={onCheckboxChange}
                            />
                          }
                          label='All Transactions'
                          sx={{ minWidth: '170px', m: 0 }}
                        />
                      </FormGroup>
                    )}
                    {isCityLedgerPosting && (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showCLTrans}
                              onChange={showOnlyCityLedgerTransactions}
                            />
                          }
                          label='City Ledger Transactions'
                          sx={{ minWidth: '170px', m: 0 }}
                        />
                      </FormGroup>
                    )}
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            defaultChecked={isDetailedFolioChecked}
                            onChange={handleDetailedFolioChange}
                          />
                        }
                        label='View Detailed Folio'
                        sx={{ minWidth: '170px', m: 0 }}
                        id='view-detailed-folio'
                      />
                    </FormGroup>
                  </Stack>
                </Stack>

                <Stack
                  sx={{
                    height: {
                      xs: '500px',
                      lg: isCityLedgerPosting ? '500px' : 'calc(100% - 38px)',
                    },
                    width: '100%',
                  }}
                >
                  <DataTable
                    apiRef={apiRef}
                    className='room-posting-datatable'
                    fullHeight
                    getRowId={(row) => row.transaction_id}
                    columns={filteredColumns}
                    rows={initialData}
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSelector
                    disableSearch
                    onRowSelectionModelChange={handleSelectionChange}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onCellClick={(params) => {
                      if (params.field === '__check__') {
                        const selectedIds = Array.from(
                          apiRef.current.getSelectedRows().keys(),
                        );
                        handleSelectionChange(selectedIds);
                      }
                    }}
                    rowSelectionModel={selectedLedgers}
                    autoPageSize={false}
                    pageSize={100}
                    loading={
                      isLoading ||
                      isFetchingFilter ||
                      isFetchingLedger ||
                      isLoadingTransfer ||
                      isSearching
                    }
                    initialState={{
                      columns: {
                        columnVisibilityModel: {
                          sub_group: false,
                          dr_cr_adj: false,
                        },
                      },
                      sorting: {
                        sortModel: [{ field: 'posting_date', sort: 'asc' }],
                      },
                    }}
                    isCellEditable={(params) =>
                      isCityLedgerPosting === true &&
                      params.field === 'collection' &&
                      params.row.item === 'City Ledger' &&
                      showCLTrans === true
                    }
                    onCellDoubleClick={(params) => {
                      if (
                        params.field === 'entry_remarks' &&
                        params.row.item !== 'City Ledger' &&
                        showCLTrans === true
                      ) {
                        handleRowDoubleClick(
                          params.row._id,
                          params.row.entry_remarks,
                        );
                      } else if (
                        isCityLedgerPosting === true &&
                        params.field === 'collection' &&
                        params.row.item === 'City Ledger' &&
                        showCLTrans === true
                      ) {
                        apiRef.current.startCellEditMode({
                          id: params.id,
                          field: params.field,
                        });
                      }
                    }}
                    processRowUpdate={(newRow) => {
                      // Check amount if between total charges and total collections
                      const charges = Number(
                        String(newRow.charges).replace(/,/g, ''),
                      );
                      let collection = Number(
                        String(newRow.collection).replace(/,/g, ''),
                      );
                      const originalCollection = Number(
                        String(newRow.originalCollection).replace(/,/g, ''),
                      );

                      if (!isNaN(collection)) {
                        if (collection > charges) collection = charges;
                        if (collection < originalCollection)
                          collection = originalCollection;
                      }

                      newRow.collection = collection;

                      // Store edited rows into clEditedRows
                      if (
                        isCityLedgerPosting &&
                        newRow.item === 'City Ledger' &&
                        collection !== originalCollection
                      ) {
                        const editedEntry: RoomPostingCLPaymentBreakdownEntry =
                          {
                            ledgerTransactionID: newRow.transaction_id,
                            currentCollection: collection.toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            ),
                            originalCollection: newRow.originalCollection,
                          };

                        // Replace previous entry if exists
                        setCLEditedRows((prev) => [
                          ...prev.filter(
                            (r) =>
                              r.ledgerTransactionID !==
                              editedEntry.ledgerTransactionID,
                          ),
                          editedEntry,
                        ]);
                      } else {
                        // Delete entry if no changes
                        setCLEditedRows((prev) =>
                          prev.filter(
                            (r) =>
                              r.ledgerTransactionID !== newRow.transaction_id,
                          ),
                        );
                      }

                      // Return the updated row (formatted number string if needed)
                      return {
                        ...newRow,
                        collection: collection.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }),
                      };
                    }}
                    onProcessRowUpdateError={(error) => console.error(error)}
                    slotProps={{
                      row: {
                        onContextMenu: handleContextMenu,
                        style: { cursor: 'context-menu' },
                      },
                    }}
                    sx={{
                      p: 0,
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </Box>

          <Divider
            orientation='vertical'
            flexItem
            sx={{ display: { xs: 'none', sm: 'block' } }}
          />
          <Divider
            orientation='horizontal'
            flexItem
            sx={{ display: { xs: 'block', sm: 'none' } }}
          />

          {/* FOLIO DETAILS */}
          <Stack
            sx={{
              width: { xs: '100%', lg: '20%' },
              p: 2,
              pb: { xs: 2, lg: 0 },
              minWidth: '230px',
            }}
            gap={1.5}
          >
            {/* Room Search */}
            {!isCityLedgerPosting && (
              <Stack>
                <Typography
                  variant='subtitle1'
                  pb={1}
                  sx={{ display: { xs: 'block', lg: 'none', xl: 'block' } }}
                >
                  Filter Records
                </Typography>

                <Stack gap={1}>
                  <TextField
                    id='room-number'
                    label='Room Number'
                    variant='outlined'
                    size='small'
                    autoComplete='off'
                    sx={{ width: '100%', minWidth: '100px', pb: '5px' }}
                    value={selectedRoomNo}
                    onChange={handleRoomNoChange}
                    onKeyDown={handleRoomNoKeyEvent}
                    InputProps={{
                      startAdornment: <NumbersRounded />,
                    }}
                    disabled={srcNav === 'Room Posting' ? false : true}
                  />

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      format='MMMM DD, YYYY'
                      label='Date'
                      value={selectedDate}
                      onChange={handleOnChangeDateEvent}
                      slotProps={{
                        field: { readOnly: true },
                        textField: { size: 'small' },
                        actionBar: { actions: ['today'] },
                        layout: {
                          sx: {
                            '& .MuiPickersCalendarHeader-switchViewButton, .MuiPickersArrowSwitcher-root button':
                              {
                                border: 0,
                                color: '#161C248F',
                                bgcolor: 'transparent',
                              },
                            '.MuiDialogActions-root button': {
                              width: '100%',
                              bgcolor: '#DFE3E8',
                              color: textColor,
                            },
                          },
                        },
                      }}
                      sx={{
                        '& .MuiInputAdornment-root button': {
                          border: 0,
                          color: '#161C248F',
                          bgcolor: 'transparent',
                        },
                        opacity: isNetLocked ? 0.5 : 'unset',
                        pointerEvents: isNetLocked ? 'none' : 'unset',
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Stack>
            )}

            {/* Folio Details */}

            <Stack gap={1}>
              {!isCityLedgerPosting && (
                <Stack>
                  <Stack
                    direction='row'
                    gap={1}
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Typography
                      variant='subtitle1'
                      sx={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Folio Balance
                    </Typography>

                    <IconButton
                      disableRipple
                      onClick={() => setShowFolio(!showFolio)}
                    >
                      {showFolio ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>

                  <Box>
                    <Stack
                      sx={{ p: '5px' }}
                      direction='row'
                      borderBottom={'1px dashed #EEE'}
                    >
                      <Typography
                        variant='subtitle1'
                        sx={{
                          width: '50%',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isLoadingSubFolio
                          ? 'loading..'
                          : `${subGroupList[1]?.caption} (${getSubGroupCount(
                              subGroupList[1]?.caption,
                            )})`}
                      </Typography>

                      <Typography
                        variant='subtitle1'
                        sx={{
                          width: '50%',
                          whiteSpace: 'nowrap',
                          textAlign: 'right',
                        }}
                      >
                        {roomPostingLedgerList.data?.balance.defaultFolio ||
                          '0.00'}
                      </Typography>
                    </Stack>

                    <Collapse in={showFolio}>
                      <Stack
                        sx={{ p: '5px' }}
                        direction='row'
                        borderBottom={'1px dashed #EEE'}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isLoadingSubFolio
                            ? 'loading..'
                            : `${subGroupList[2]?.caption} (${getSubGroupCount(
                                subGroupList[2]?.caption,
                              )})`}
                        </Typography>

                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                          }}
                        >
                          {roomPostingLedgerList.data?.balance.folioA || '0.00'}
                        </Typography>
                      </Stack>

                      <Stack
                        sx={{ p: '5px' }}
                        direction='row'
                        borderBottom={'1px dashed #EEE'}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isLoadingSubFolio
                            ? 'loading..'
                            : `${subGroupList[3]?.caption} (${getSubGroupCount(
                                subGroupList[3]?.caption,
                              )})`}
                        </Typography>

                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                          }}
                        >
                          {roomPostingLedgerList.data?.balance.folioB || '0.00'}
                        </Typography>
                      </Stack>

                      <Stack
                        sx={{ p: '5px' }}
                        direction='row'
                        borderBottom={'1px dashed #EEE'}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isLoadingSubFolio
                            ? 'loading..'
                            : `${subGroupList[4]?.caption} (${getSubGroupCount(
                                subGroupList[4]?.caption,
                              )})`}
                        </Typography>

                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                          }}
                        >
                          {roomPostingLedgerList.data?.balance.folioC || '0.00'}
                        </Typography>
                      </Stack>

                      <Stack
                        sx={{ p: '5px' }}
                        direction='row'
                        borderBottom={'1px dashed #EEE'}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isLoadingSubFolio
                            ? 'loading..'
                            : `${subGroupList[5]?.caption} (${getSubGroupCount(
                                subGroupList[5]?.caption,
                              )})`}
                        </Typography>

                        <Typography
                          variant='subtitle1'
                          sx={{
                            width: '50%',
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                          }}
                        >
                          {roomPostingLedgerList.data?.balance.folioD || '0.00'}
                        </Typography>
                      </Stack>
                    </Collapse>
                  </Box>
                </Stack>
              )}

              <Stack
                sx={{ justifyContent: 'flex-end' }}
                direction={{ xs: 'column', lg: 'column' }}
                width='100%'
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    pt: '20px',
                  }}
                >
                  {'Total Charges'}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '18px',
                    // p: '5px',
                    pr: '5px',
                    textAlign: 'right',
                    borderBottom: '1px dashed #EEE',
                  }}
                >
                  {roomPostingLedgerList.data?.summary.totalCharges || '0.00'}
                </Typography>

                <Typography
                  sx={{
                    pt: { xs: '0px', lg: '5px' },
                    fontSize: '14px',
                  }}
                >
                  {'Total Collections'}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '18px',
                    // p: '5px',
                    pr: '5px',
                    textAlign: 'right',
                    borderBottom: '1px dashed #EEE',
                  }}
                >
                  {roomPostingLedgerList.data?.summary.totalCollection ||
                    '0.00'}
                </Typography>

                <Typography
                  sx={{
                    pt: '5px',
                    fontSize: '14px',
                  }}
                >
                  {'Total Adjustments'}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '18px',
                    // p: '5px',
                    pr: '5px',
                    textAlign: 'right',
                    borderBottom: '1px dashed #EEE',
                  }}
                >
                  {roomPostingLedgerList.data?.summary.totalAdjustment ||
                    '0.00'}
                </Typography>

                <Typography
                  sx={{
                    pt: '5px',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {'Total Payables'}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '18px',
                    // p: '5px',
                    pr: '5px',
                    pb: '2px',
                    textAlign: 'right',
                    // borderBottom: '1px dashed #EEE',
                    color: 'red',
                  }}
                >
                  {roomPostingLedgerList.data?.summary.totalPayables || '0.00'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* MENUS , MODALS , DIALOGS */}
      <>
        <Menu
          id='room-posting-menu'
          open={contextMenu !== null && menuMode !== 'CREDIT'}
          onClose={handleClose}
          anchorReference='anchorPosition'
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          componentsProps={{
            root: {
              onContextMenu: (e) => {
                e.preventDefault();
                handleClose();
              },
            },
          }}
          // sx={{ width: '18vw' }}
        >
          <>
            {/* ✅ DEBIT → Void ONLY */}
            {menuMode === 'DEBIT' && (
              <MenuItem
                onClick={() => {
                  handleVoidItem();
                  handleSubMenuClose('');
                }}
              >
                Void
              </MenuItem>
            )}

            {/* ❌ CREDIT → show NOTHING */}
            {menuMode === 'CREDIT' && null}

            {menuMode === 'ALL' && (
              <>
                <MenuItem
                  id='edit-remarks'
                  onClick={() => {
                    if (selectedLedgers.length === 0) {
                      onShowInfoDialog({
                        desc: `Please select an item(s) to proceed.`,
                      });
                      return;
                    }

                    const selectedItems = getSelectedItems();

                    const selectedIDs = selectedItems
                      .map((row) => String(row._id))
                      .join(',');
                    const remarks = selectedItems[0]?.entry_remarks || '';

                    setTransIds(selectedIDs);
                    setSelRemarks(remarks);
                    setSelectedContextMenu('ER');
                    modalDisclosure.onToggle();
                    setContextMenu(null);
                  }}
                >
                  Edit Remarks
                </MenuItem>
                <MenuItem
                  id='apply-discount'
                  onClick={() => {
                    handleSelectedContext('AD');
                  }}
                >
                  Apply Discount
                </MenuItem>
                <MenuItem
                  id='modify-line'
                  onClick={() => {
                    handleSelectedContext('ML');
                  }}
                >
                  Modify Line
                </MenuItem>
                {bookingStatus !== 'pending' && (
                  <MenuItem
                    onClick={() => {
                      openSecCodeDialog(async (res) => {
                        if (!res?.data.isValid) return;
                        handleSelectedContext('LT');
                      }, 'allow_lt'); // edit priv
                    }}
                  >
                    Line Transfer
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    handleVoidItem();
                    handleSubMenuClose('');
                  }}
                >
                  Void
                </MenuItem>

                <MenuItem
                  onClick={(e) => {
                    handleSubContextMenu(e);
                  }}
                >
                  Transfer To..
                </MenuItem>
              </>
            )}
          </>
        </Menu>
        <Menu
          open={subContextMenu !== null}
          onClose={() => {
            handleSubMenuClose('');
          }}
          anchorReference='anchorPosition'
          anchorPosition={
            subContextMenu !== null
              ? { top: subContextMenu.mouseY, left: subContextMenu.mouseX }
              : undefined
          }
          componentsProps={{
            root: {
              onContextMenu: (e) => {
                e.preventDefault();
                handleSubMenuClose('');
              },
            },
          }}
          // sx={{ width: '18vw' }}
        >
          {//Sub Group Default List
          // options={subGroupList?.map((option) => option?.caption)}
          subGroupList?.map(
            (view, index) =>
              view?.caption !== 'All' &&
              view?.caption !== 'Charges' &&
              view?.caption !== 'Payments' && [
                <MenuItem
                  key={index}
                  onClick={() => {
                    handleSubMenuClose(view?.caption);
                  }}
                >
                  {view?.caption}
                </MenuItem>,
              ],
          )}
        </Menu>
        <ModalWrapper
          disableOnClose
          boxStyle={{
            width: {
              xs: '100%',
              md:
                selectedContextMenu === 'AD'
                  ? '830px'
                  : selectedContextMenu === 'ML' ||
                      selectedContextMenu === 'ER' ||
                      selectedContextMenu === 'VD'
                    ? '444px'
                    : selectedContextMenu === 'LT'
                      ? '650px'
                      : '764px',
            },
          }}
          modalDisclosure={modalDisclosure}
          id='room-posting-table-modal'
        >
          <>
            {selectedContextMenu == 'AD' && (
              <FORoomPostingDiscountForm
                handleClose={modalDisclosure.onClose}
                selectedIDs={transIds}
                ledgerRefetch={ledgerRefetch}
                useProperty={useProperty}
                handleConfig={() => {}}
                fromModule='Room Posting'
                totalPax={roomPostingLedgerList?.data?.totalPax}
                maxAmount={totalAmount}
              />
            )}
            {selectedContextMenu == 'ML' && (
              <FORoomPostingModifyLineForm
                handleClose={modalDisclosure.onClose}
                selectedIDs={transIds}
                withDisc={selectedWithDiscount}
                withVat={selectedWithVAT}
                appliedSC={appliedSC}
              />
            )}
            {selectedContextMenu == 'LT' && (
              <FORoomPostingLineTransferForm
                handleClose={modalDisclosure.onClose}
                selectedIDs={transIds}
              />
            )}
            {selectedContextMenu == 'ER' && (
              <FoRoompostingItemremarksForm
                handleClose={modalDisclosure.onClose}
                selectedIDs={transIds}
                curRemarks={selRemarks}
              />
            )}
            {selectedContextMenu == 'VD' && (
              <FoRoompostingItemvoidForm
                handleClose={modalDisclosure.onClose}
                selectedIDs={transIds}
                handleVoidedAR={(voidARNos) => {
                  setVoidARs(voidARNos);
                  if (voidARNos) {
                    printARDisclosure.onOpen();
                  }
                }}
                isSpecificBooking={isSpecificBooking}
              />
            )}
          </>
        </ModalWrapper>
        <CASARVoucher
          ar_no={voidARNos}
          isOpen={printARDisclosure.isOpen}
          onClose={() => {
            printARDisclosure.onClose();
            //forceClose && onCloseForm();
          }}
          tranType={'VAR'}
        />
        <DialogInformation />

        {/* MESSAGE DIALOG */}
        <DialogMessage
          isOpen={dialogMessage.isOpen}
          error={null}
          onClose={() => {
            dialogMessage.onClose();
            setErrorTitle('');
            setErrorMessage('');
          }}
          message={{
            title: errorTitle,
            desc: errorMessage,
            status: 'warning',
          }}
        />

        {/* VOID RESTO CHARGE VALIDATION */}
        <ConfirmationDialog
          open={restoChargeValidation.isOpen}
          title='Unable to Void Selected Transaction(s)'
          desc={
            <Stack gap='8px' alignItems='flex-start'>
              <Typography>
                Some selected transaction(s) cannot be voided from Front Office
                because they were generated from POS. Please process these
                transaction(s) from the POS system.
              </Typography>

              <Stack>
                <Typography>Excluded Transaction(s):</Typography>
                <Box component='ul' sx={{ margin: 0, paddingLeft: 3 }}>
                  {restoChargeItemList.map((item, index) => (
                    <li key={index}>
                      <Typography component='span'>{item}</Typography>
                    </li>
                  ))}
                </Box>
              </Stack>

              <Typography>
                The remaining eligible transaction(s) will proceed with the void
                process. Do you want to Continue?
              </Typography>
            </Stack>
          }
          onConfirm={async () => {
            // Recalculate from the current selection so the void modal only
            // receives FO items even if the selection changed while confirming.
            const currentFrontOfficeIDs = getSelectedItems()
              .filter((item) => item.is_fo === 1)
              .map((row) => String(row._id))
              .join(',');
            restoChargeValidation.onClose();
            if (!currentFrontOfficeIDs) {
              setRestoChargeItemList([]);
              return;
            }
            setTransIds(currentFrontOfficeIDs);
            setSelectedContextMenu('VD');
            modalDisclosure.onToggle();
          }}
          onClose={() => {
            restoChargeValidation.onClose();
            setRestoChargeItemList([]);
          }}
          confirmButtonTitle='Continue'
          cancelButtonTitle='Cancel'
          switchButton
          backBtnProps={{ variant: 'outlined' }}
          confirmBtnProps={{ variant: 'outlined' }}
          icon={Warning}
          iconColor='warning'
          dialogStatus='warning'
        />
      </>
    </>
  );
};

export default RoomPostingDataTable;
